import { useEffect, useRef } from 'react';

interface HeroSectionProps {
  onConsultationClick: () => void;
}

const stats = [
  { value: '45%', label: 'Avg. Reduction in Missed Calls' },
  { value: '32%', label: 'More Booked Appointments' },
  { value: '60%', label: 'Faster Response Times' },
  { value: '4', label: 'High-Stakes Industries Served' },
];

export default function HeroSection({ onConsultationClick }: HeroSectionProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
      setTimeout(() => {
        el.style.transition = 'opacity 1s ease, transform 1s ease';
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      }, 150);
    }
    const sEl = statsRef.current;
    if (sEl) {
      sEl.style.opacity = '0';
      setTimeout(() => {
        sEl.style.transition = 'opacity 1.2s ease';
        sEl.style.opacity = '1';
      }, 700);
    }
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#F5F5F7] dark:bg-[#0B0B0C]">

      {/* Background image — dark only */}
      <div
        className="absolute inset-0 opacity-0 dark:opacity-100 transition-opacity duration-700"
        style={{
          backgroundImage: `url("https://public.readdy.ai/ai/img_res/47ce876027796ebc6e9b13db6de73dd7.jpg")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 opacity-0 dark:opacity-100 bg-gradient-to-b from-[#0B0B0C]/80 via-[#0B0B0C]/60 to-[#0B0B0C]/95 transition-opacity duration-700" />

      {/* Grid pattern (dark mode) */}
      <div
        className="absolute inset-0 opacity-0 dark:opacity-100 pointer-events-none transition-opacity duration-700"
        style={{
          backgroundImage: `linear-gradient(rgba(201,168,64,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,64,0.04) 1px, transparent 1px)`,
          backgroundSize: '72px 72px',
        }}
      />

      {/* Light mode subtle grid */}
      <div
        className="absolute inset-0 opacity-100 dark:opacity-0 pointer-events-none transition-opacity duration-700"
        style={{
          backgroundImage: `linear-gradient(rgba(201,168,64,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,64,0.06) 1px, transparent 1px)`,
          backgroundSize: '72px 72px',
        }}
      />

      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-40 h-40 border-l border-t border-[#C9A840]/20 pointer-events-none" />
      <div className="absolute top-0 right-0 w-40 h-40 border-r border-t border-[#C9A840]/20 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-40 h-40 border-l border-b border-[#C9A840]/20 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-40 h-40 border-r border-b border-[#C9A840]/20 pointer-events-none" />

      {/* Main content */}
      <div ref={heroRef} className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 text-center pt-32 pb-16">
        {/* Label */}
        <div className="inline-flex items-center gap-3 mb-10">
          <span className="w-10 h-px bg-[#5BBFB5]" />
          <span className="text-[#C9A840] text-xs font-semibold" style={{ letterSpacing: '0.25em', textTransform: 'uppercase' }}>
            AI Operational Intelligence
          </span>
          <span className="w-10 h-px bg-[#5BBFB5]" />
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-[#1D1D1F] dark:text-[#F2F0EC] leading-[1.05] tracking-tight mb-8">
          Precision Intelligence.
          <br />
          <span className="text-[#C9A840]">Operational Authority.</span>
        </h1>

        {/* Thin divider */}
        <div className="w-20 h-px bg-[#C9A840]/60 mx-auto mb-10" />

        {/* Sub */}
        <p className="text-[#6E6E73] dark:text-[#96969E] text-xl md:text-2xl leading-relaxed max-w-2xl mx-auto mb-14 font-light">
          Auditore AI builds structured intelligence systems for organizations where
          execution, compliance, and control matter most.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-24">
          <button
            onClick={onConsultationClick}
            className="whitespace-nowrap w-full sm:w-auto px-12 py-4 bg-[#C9A840] text-[#0B0B0C] text-sm font-bold hover:bg-[#A08840] transition-all duration-300 cursor-pointer"
            style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            Request Consultation
          </button>
          <button
            onClick={() => scrollTo('#solution')}
            className="whitespace-nowrap w-full sm:w-auto px-12 py-4 border border-[#D2D2D7] dark:border-[#252528] text-[#6E6E73] dark:text-[#96969E] text-sm font-medium hover:border-[#C9A840]/60 hover:text-[#1D1D1F] dark:hover:text-[#F2F0EC] transition-all duration-300 cursor-pointer"
            style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            View Systems
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div ref={statsRef} className="relative z-10 w-full border-t border-[#D2D2D7]/60 dark:border-[#252528] bg-white/50 dark:bg-[#0B0B0C]/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D2D2D7]/60 dark:divide-[#252528]">
            {stats.map((s) => (
              <div key={s.value} className="py-8 px-6 text-center">
                <span className="block text-3xl md:text-4xl font-bold text-[#C9A840] mb-2 tracking-tight">{s.value}</span>
                <span className="block text-[#6E6E73] dark:text-[#96969E] text-xs" style={{ letterSpacing: '0.06em' }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
